import React, { useEffect, useMemo, useState } from 'react';
import { DataTable, Pagination, PriorityBadge, SearchInput, Select, StatusBadge, Tabs } from '../../ui';
import SectionCard from './SectionCard';
import useDashboardSection from './useDashboardSection';
import { fmtCount, fmtPct, fmtRelative } from './format';

const KINDS = [
  { id: 'all', label: 'All' },
  { id: 'failing', label: 'Failing now' },
  { id: 'new', label: 'New' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'flaky', label: 'Flaky' },
];

const EMPTY_COPY = {
  all: 'No test failed in this period.',
  failing: 'No test is currently failing.',
  new: 'No new failures compared with the previous period.',
  recurring: 'No test failed more than once in this period.',
  flaky: 'No test meets the flaky rule.',
};

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export default function FailuresTable({ baseParams, request, navigateTo, projectId }) {
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('last_failed');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const term = useDebounced(search.trim(), 300);

  useEffect(() => {
    if (request?.kind) {
      setKind(request.kind);
      setPage(1);
    }
  }, [request]);

  useEffect(() => setPage(1), [term, sort, pageSize, baseParams]);

  const params = useMemo(
    () => ({
      ...baseParams,
      kind,
      sort,
      page,
      page_size: pageSize,
      ...(term ? { search: term } : {}),
    }),
    [baseParams, kind, sort, page, pageSize, term]
  );
  const { data, loading, error, retry, refreshing, reload } = useDashboardSection('failures', params);

  useEffect(() => {
    if (request?.refresh) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.refresh]);

  const counts = data?.counts || {};
  const openExecution = (row) => {
    const failure = row.last_failure;
    if (!failure) {
      navigateTo(`test-cases/${row.test_case_id}?project_id=${projectId}`);
      return;
    }
    const q = new URLSearchParams({ type: 'case', entity_id: String(row.test_case_id), name: row.name || '' });
    navigateTo(`executions/${failure.execution_id}?${q.toString()}`);
  };

  const columns = [
    {
      key: 'name',
      header: 'Test',
      render: (row) => (
        <div className="pqd-fail-test">
          <button
            type="button"
            className="pqd-fail-test__name"
            onClick={(e) => {
              e.stopPropagation();
              navigateTo(`test-cases/${row.test_case_id}?project_id=${projectId}`);
            }}
          >
            {row.name || `Test case #${row.test_case_id}`}
          </button>
          <div className="pqd-fail-test__tags">
            {row.priority && <PriorityBadge priority={row.priority} />}
            {row.is_new && <span className="pqd-tag pqd-tag--danger">New</span>}
            {row.is_recurring && <span className="pqd-tag pqd-tag--warning">Recurring</span>}
            {row.is_flaky && (
              <span
                className="pqd-tag pqd-tag--info"
                title={
                  row.flaky_window
                    ? `${row.flaky_window.failures} failures, ${row.flaky_window.passes} passes, ${row.flaky_window.flips} flips`
                    : undefined
                }
              >
                Flaky
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'latest',
      header: 'Latest',
      width: 110,
      render: (row) =>
        row.latest_status ? (
          <StatusBadge status={row.latest_status}>{row.latest_status}</StatusBadge>
        ) : (
          <span className="pqd-muted" title="No result in the selected period">Not run</span>
        ),
    },
    {
      key: 'failures',
      header: 'Failures',
      width: 120,
      render: (row) => (
        <div className="pqd-num">
          <strong>{fmtCount(row.failures)}</strong>
          <span className="pqd-muted"> / {fmtCount(row.runs)} runs</span>
          {row.failure_rate != null && <div className="pqd-muted pqd-small">{fmtPct(row.failure_rate)} failed</div>}
        </div>
      ),
    },
    {
      key: 'last_failed',
      header: 'Last failed',
      width: 120,
      render: (row) => <span title={row.last_failure?.at || row.last_failed_at || ''}>{fmtRelative(row.last_failure?.at || row.last_failed_at)}</span>,
    },
    {
      key: 'error',
      header: 'Last error (from logs)',
      render: (row) =>
        row.last_failure?.error_summary ? (
          <code className="pqd-error-line" title={row.last_failure.error_summary}>
            {row.last_failure.error_summary}
          </code>
        ) : (
          <span className="pqd-muted">No log captured</span>
        ),
    },
    {
      key: 'action',
      header: 'Next step',
      width: 170,
      render: (row) => (
        <button
          type="button"
          className="rt-btn rt-btn--secondary rt-btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            openExecution(row);
          }}
        >
          {row.action?.label || 'Inspect'}
        </button>
      ),
    },
  ];

  return (
    <SectionCard
      id="pqd-failures"
      title="Failures & flaky tests"
      subtitle={data?.flaky_rule?.description ? `Flaky rule: ${data.flaky_rule.description}.` : undefined}
      error={!data && error}
      onRetry={retry}
      refreshing={refreshing}
      loading={false}
    >
      <div className="pqd-toolbar">
        <Tabs
          tabs={KINDS.map((k) => ({ ...k, count: data ? counts[k.id] ?? 0 : undefined }))}
          activeId={kind}
          onChange={(id) => {
            setKind(id);
            setPage(1);
          }}
        />
        <div className="pqd-toolbar__right">
          <SearchInput value={search} onChange={setSearch} placeholder="Search tests…" />
          <Select size="sm" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort failures">
            <option value="last_failed">Most recent failure</option>
            <option value="failures">Most failures</option>
            <option value="priority">Priority</option>
            <option value="name">Name</option>
          </Select>
        </div>
      </div>
      {error && data && <p className="pqd-field-error" role="alert">{error}</p>}
      <DataTable
        columns={columns}
        rows={data?.items || []}
        rowKey="test_case_id"
        loading={loading}
        skeletonRows={5}
        onRowClick={openExecution}
        emptyTitle={term ? 'No tests match your search' : EMPTY_COPY[kind]}
        emptyDescription={term ? 'Try a different name.' : 'Failures from the selected period and environment appear here.'}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total || 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        loading={loading || refreshing}
      />
    </SectionCard>
  );
}
